import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Divider,
  Stack,
  alpha,
  IconButton,
  Avatar,
  Tooltip,
  Alert,
  CircularProgress,
  AlertTitle,
} from "@mui/material";
import {
  GppGoodOutlined,
  RadioButtonChecked,
  RadioButtonUnchecked,
  Shield,
  Verified,
  AccountCircle,
  Logout,
  SupportAgentOutlined,
  SupportAgentTwoTone,
  SupportTwoTone,
} from "@mui/icons-material";
import { Navigate, useNavigate } from "react-router-dom";

import { useSelector } from "react-redux";
import { initializePaddle } from "@paddle/paddle-js";
import { green, grey } from "@mui/material/colors";
import { getProducts } from "api/app-billing";
import { useQuery } from "react-query";
import Dinero from "dinero.js";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Logo from "images/dalvado-logo.png";
import { useApp } from "contexts/AppContext";
import { useSnackbar } from "notistack";
import { id } from "react-date-range/dist/locale";
import ConsoleHelper from "utils/ConsoleHelper";

// A simple chip-like component that uses alpha to create a tinted background
const OffChip = ({ label, textColor }) => {
  const backgroundColor = alpha(textColor, 0.15); // Subtle tint

  return (
    <Box
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.25,
        borderRadius: "9999px",
        color: textColor,
        backgroundColor,
        fontWeight: 700,
        fontSize: "0.775rem",
        lineHeight: 1.6,
      }}
    >
      {label}
    </Box>
  );
};

const LocalizedPrice = ({ priceObj }) => {
  const [localizedPrice, setLocalizedPrice] = useState(
    priceObj.localizedPrice || "Loading..."
  );
  useEffect(() => {
    if (window.Paddle && window.Paddle.PricePreview) {
      window.Paddle.PricePreview({
        items: [{ priceId: priceObj.id, quantity: 1 }],
      })
        .then((result) => {
          console.log("result", result);
          const fetchedPrice =
            result?.data?.details?.lineItems?.[0]?.formattedTotals?.subtotal;
          setLocalizedPrice(fetchedPrice || priceObj.localizedPrice || "N/A");
        })
        .catch((error) => {
          console.error("Error fetching localized price:", error);
          setLocalizedPrice(priceObj.localizedPrice || "N/A");
        });
    }
  }, [priceObj, priceObj.localizedPrice]);

  return <>{localizedPrice}</>;
};

const SubscriptionPageV2 = () => {
  const app = useApp();

  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation();

  const [paddleCheckoutCompleted, setPaddleCheckoutCompleted] = useState(false);
  const paddleRef = useRef(null);
  const features = [
    {
      title: "subscription.feature1",
      description: "subscription.feature1Description",
    },
    {
      title: "subscription.feature2",
      description: "subscription.feature2Description",
    },
    {
      title: "subscription.feature3",
      description: "subscription.feature3Description",
    },
  ];
  // Define your plan data

  // Parse prices from strings (e.g. "$49" => 49)
  //   const parsePrice = (priceStr) =>
  //     parseFloat(priceStr.replace(/[^0-9.]/g, "")) || 0;

  // Calculate savings
  //   const monthlyPlanPrice = parsePrice(
  //     plans.find((p) => p.name === "Monthly").price
  //   );
  //   const yearlyPlanPrice = parsePrice(
  //     plans.find((p) => p.name === "Yearly").price
  //   );

  //   const monthlyYearCost = monthlyPlanPrice * 12;
  //   const difference = monthlyYearCost - yearlyPlanPrice;
  //   const differencePercentage = Math.round((difference / monthlyYearCost) * 100);

  // Paddle Payment Logic
  const handlePayment = () => {
    let priceId = "";

    priceId = selectedPlan;
    if (paddleRef.current) {
      // Additional check for owner
      if (app.state.organization.owner.email !== app.state.user.email) {
        enqueueSnackbar("You are not the owner of this organization", {
          variant: "error",
        });
        return;
      }
      ConsoleHelper("Paddle Checkout opened with priceId:", app.state);
      ConsoleHelper(
        "paddle customer id",
        app.state.organization.paddleCustomerId
      );
      const paddleCustomerId = app.state?.organization?.paddleCustomerId;
      paddleRef.current.Checkout.open({
        settings: {
          allowLogout: false,
          displayMode: "overlay",
          variant: "one-page",
          locale: app.state.user.language,
        },
        items: [{ priceId, quantity: 1 }],
        customer: {
          email: app.state.user.email,
          id: paddleCustomerId,
        },
        customData: {
          user: {
            id: app.state.user.id,
            email: app.state.user.email,
            name: app.state.user.name,
            organizationId: app.state.organization.id,
          },
        },
      });
    }
  };
  useEffect(() => {
    // Poll every 5 seconds
    const interval = setInterval(() => {
      app.fetchMe();
    }, 5000);

    return () => clearInterval(interval);
  }, [app.fetchMe]);

  useEffect(() => {
    console.log(process.env);
    const appEnv = process.env.REACT_APP_APP_ENV.toLowerCase();
    ConsoleHelper("App environment:", appEnv);
    let paddleEnvironment = "sandbox";
    let paddleToken =
      process.env.REACT_APP_PADDLE_CLIENT_SIDE_TOKEN_SANDBOX ||
      process.env.REACT_APP_PADDLE_CLIENT_SIDE_TOKEN;

    switch (appEnv) {
      case "production":
        paddleEnvironment = "production";
        paddleToken =
          process.env.REACT_APP_PADDLE_CLIENT_SIDE_TOKEN_PROD ||
          process.env.REACT_APP_PADDLE_CLIENT_SIDE_TOKEN;
        break;
      case "staging":
      case "development":
      default:
        paddleEnvironment = "sandbox";
        paddleToken = process.env.REACT_APP_PADDLE_CLIENT_SIDE_TOKEN_SANDBOX;
        break;
    }

    initializePaddle({
      environment: paddleEnvironment,
      token: paddleToken,
      eventCallback: (event) => {
        if (event.name === "checkout.completed") {
          // Your custom logic here
          console.log("Checkout completed successfully:", event);
          // app.fetchMe();
          setPaddleCheckoutCompleted(true);
          if (paddleRef.current) {
            setTimeout(() => {
              paddleRef.current.Checkout.close();
            }, 2000); // Wait for 2 seconds before closing
          }
        }
      },
    }).then((paddle) => {
      paddleRef.current = paddle;
    });
  }, []);

  const { data: prices, error, isLoading } = useQuery("products", getProducts);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // When prices load, if nothing is selected yet, default to the first plan.
  useEffect(() => {
    if (prices?.length && selectedPlan === null) {
      setSelectedPlan(prices[0].id);
    }
  }, [prices, selectedPlan]);

  const handlePlanClick = (planName) => {
    setSelectedPlan(planName);
  };

  const handleLogout = () => {
    app.logout();
  };
  // if (app.state.subscription.status === "active") {
  console.log("Subscription is active, redirecting to dashboard");
  //   return <Navigate to="/*" replace />;
  // }
  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase();
  };
  if (app.state.organization === null) {
    return <Typography>There was a problem</Typography>;
  }
  if (error) {
    console.error("Error fetching products:", error);
    return <Typography>Error loading subscription plans</Typography>;
  }
  const isOwner = app.state.organization.owner.email === app.state.user.email;
  if (paddleCheckoutCompleted) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Box>
          <Typography variant="h6" sx={{ ml: 2 }}>
            {t("subscription.processingSubscription")}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (process.env.REACT_APP_COSTI_LOCK) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          color: "#333333",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Grid container spacing={4}>
            <Grid item xs={11}>
              <Alert>
                <AlertTitle>{t("subscription.costiSuspended")}</AlertTitle>
              </Alert>
            </Grid>
            <Grid item xs={1}>
              <Tooltip title={t("auth.logout")}>
                <IconButton color="inherit" onClick={handleLogout}>
                  <Logout />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#ffffff",
        color: "#333333",
        py: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Grid container spacing={4}>
          <Grid item xs={12} display="flex">
            <Stack
              direction="row"
              spacing={1}
              flex={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center">
                <img src={Logo} alt="Dalvado Logo" style={{ height: 40 }} />
              </Box>
              <Box display="flex" alignItems="center">
                {/* Optionally include an avatar */}
                <Tooltip title={t("auth.logout")}>
                  <IconButton color="inherit" onClick={handleLogout}>
                    <Logout />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {/* Alert banner for non-owner users */}
        {!isOwner && (
          <Box my={2}>
            <Alert severity="warning">{t("subscription.nonOwnerAlert")}</Alert>
          </Box>
        )}

        {/* LEFT COLUMN: Plan Selection (only show for owner, or hide the button for non-owner) */}
        {isOwner && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}
              >
                {t("subscription.title")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#666666", mb: 3, maxWidth: 360 }}
              >
                {t("subscription.description")}
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t("subscription.choosePlan")}
              </Typography>
              {isLoading ? (
                <Typography>Loading...</Typography>
              ) : (
                prices.map((plan) => {
                  const isSelected = selectedPlan === plan?.id;
                  return (
                    <Box
                      key={plan.id}
                      onClick={() => handlePlanClick(plan.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${
                          isSelected ? "#1e90ff" : "#dddddd"
                        }`,
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#f2faff" : "#ffffff",
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#f9f9f9",
                        },
                      }}
                    >
                      <Box mr={"15px"}>
                        {isSelected ? (
                          <RadioButtonChecked sx={{ color: "#1e90ff" }} />
                        ) : (
                          <RadioButtonUnchecked sx={{ color: "#999999" }} />
                        )}
                      </Box>
                      <Box>
                        <Stack
                          direction={"row"}
                          spacing={1}
                          alignItems={"center"}
                        >
                          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                            {t(`subscription.${plan.billingCycle.interval}`)}
                          </Typography>
                          {plan.billingCycle.interval === "year" && (
                            <OffChip
                              label={t("subscription.yearlySave")}
                              textColor="#1e90ff"
                            />
                          )}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="baseline"
                        >
                          <Typography
                            variant="h3"
                            sx={{ fontWeight: 600, color: "#333333" }}
                          >
                            <LocalizedPrice priceObj={plan} />
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  );
                })
              )}

              {isOwner && (
                <Stack alignItems="center">
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 2,
                      backgroundColor: "#1e90ff",
                      "&:hover": { backgroundColor: "#187bcd" },
                    }}
                    onClick={handlePayment}
                  >
                    {t("subscription.startSubscription")}
                  </Button>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    <GppGoodOutlined />
                    <Typography fontWeight={500} color={grey[800]}>
                      {t("subscription.noStringsAttached")}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Grid>

            {/* RIGHT COLUMN: "How it works" or Features */}
            <Grid item xs={12} md={6}>
              <Box
                borderRadius="8px"
                p={3}
                bgcolor="#f7f7f7"
                sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
              >
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  {t("subscription.rightPanelTitle")}
                </Typography>
                <Box sx={{ borderRadius: 2, p: 2 }}>
                  {features.map((feature, index) => (
                    <div key={index}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: grey[800] }}
                      >
                        <strong>{t(feature.title)}</strong>
                        <br />
                        {t(feature.description)}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                    </div>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Support Section */}
        <Box mt={20} textAlign="center">
          <SupportTwoTone sx={{ fontSize: 40, color: green[500], mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {t("subscription.supportLabel")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666666", mb: 2 }}>
            {t("subscription.supportDescription")}
          </Typography>
          <Button
            variant="outlined"
            color="black"
            onClick={() => console.log("Contact Support clicked")}
          >
            {t("subscription.supportContactButton")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SubscriptionPageV2;
